import { GridCellProps, GridCustomCellProps } from '@progress/kendo-react-grid';
import { Moment } from 'moment';
import { useTranslation } from 'react-i18next';
import useBreakpoints from '../../../../../../../common/hooks/useBreakpoints';
import Button from '../../../../../../../common/widgets/Button';
import { GridCellTd } from '../../../../../../../common/widgets/Grid';
import Icons from '../../../../../../../common/widgets/Icons';
import PopoverDialog from '../../../../../../../common/widgets/PopoverDialog';
import { resources } from '../../../../../resources/resources';

export const SubmittedCell = (props: GridCustomCellProps) => {
  const { dataItem } = props;
  const { t: translate } = useTranslation();

  const activeColor = '#F0F7FF';
  return dataItem.isSubmitted ? (
    <GridCellTd>
      <span
        className="grid-status-badge"
        style={{
          backgroundColor: `${activeColor}`,
        }}
      >
        {translate(resources.admin.timesheets.badge.submitted)}
      </span>
    </GridCellTd>
  ) : (
    <GridCellTd>
      <span></span>
    </GridCellTd>
  );
};

export const ApprovedCell = (props: GridCustomCellProps) => {
  const { dataItem } = props;
  const { t: translate } = useTranslation();

  const activeColor = '#81C1054D';
  return dataItem.isApproved ? (
    <GridCellTd>
      <span
        className="grid-status-badge"
        style={{
          backgroundColor: `${activeColor}`,
        }}
      >
        {translate(resources.admin.timesheets.badge.approved)}
      </span>
    </GridCellTd>
  ) : (
    <GridCellTd>
      <span></span>
    </GridCellTd>
  );
};

export interface ActionsCellProps extends GridCustomCellProps {
  handleResendEmail: (employeeId: number) => void;
  handleSeeSummary: (
    employeeId: number,
    employeeName: string,
    month: Moment,
  ) => void;
  selectedMonth: Moment;
}

export const ActionsCell = (props: ActionsCellProps) => {
  const { isTablet } = useBreakpoints();
  const { dataItem, selectedMonth, handleResendEmail, handleSeeSummary } =
    props;
  const { t: translate } = useTranslation();

  const showSendEmailAction: boolean =
    !dataItem.isSubmitted && dataItem.employeeId != 0;
  const showSeeSummaryAction: boolean =
    dataItem.isSubmitted && dataItem.employeeId != 0;

  return (
    <GridCellTd {...props} className="grid-actions-cell">
      {!isTablet ? (
        <span className="grid-actions">
          {showSendEmailAction && (
            <Button
              className="btn-link grid-action-nav"
              onClick={() => {
                handleResendEmail(dataItem.employeeId);
              }}
              disabled={!dataItem.hasEmailTemplate}
            >
              <span className="btn-icon">
                <Icons.ResendMail />
              </span>
              <span className="btn-text">
                {translate(resources.admin.timesheets.grid.actions.sendEmail)}
              </span>
            </Button>
          )}
          {showSeeSummaryAction && (
            <button
              className="btn-link grid-action-nav"
              onClick={() =>
                handleSeeSummary(
                  dataItem.employeeId,
                  dataItem.employeeFullName,
                  selectedMonth,
                )
              }
            >
              <span className="btn-icon">
                <Icons.ArrowButton />
              </span>
              <span className="btn-text">
                {translate(resources.admin.timesheets.grid.actions.seeSummary)}
              </span>
            </button>
          )}
        </span>
      ) : (
        <PopoverDialog
          className="popover-actions-list"
          placement="bottom"
          closeFromContent
          type="click"
          appendTo={document.body}
          content={
            <>
              {showSendEmailAction && (
                <Button
                  className="btn"
                  onClick={() => handleResendEmail(dataItem.employeeId)}
                  disabled={!dataItem.hasEmailTemplate}
                >
                  {translate(resources.admin.timesheets.grid.actions.sendEmail)}
                </Button>
              )}
              {showSeeSummaryAction && (
                <button
                  className="btn"
                  onClick={() =>
                    handleSeeSummary(
                      dataItem.employeeId,
                      dataItem.employeeFullName,
                      selectedMonth,
                    )
                  }
                >
                  {translate(
                    resources.admin.timesheets.grid.actions.seeSummary,
                  )}
                </button>
              )}
            </>
          }
        >
          <a className="mobile-admin-action"></a>
        </PopoverDialog>
      )}
    </GridCellTd>
  );
};

export const employeeFullNameCell = ({ dataItem }: GridCellProps) => (
  <GridCellTd>
    <strong>{dataItem.employeeFullName}</strong>
  </GridCellTd>
);

export const emailCell = ({ dataItem }: GridCellProps) => (
  <GridCellTd className="grid-email">{dataItem.email}</GridCellTd>
);

export const companyNameCell = ({ dataItem }: GridCellProps) => (
  <GridCellTd>{dataItem.companyName}</GridCellTd>
);
